import type { ApiResponse } from '@maker-toolchest/shared';
import { useAuthStore, getAccessToken, isTokenExpired } from '@/stores/auth.js';

const API_URL = import.meta.env.VITE_API_URL || '';

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise: Promise<boolean> | null = null;

class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    retry = true
  ): Promise<ApiResponse<T>> {
    // Proactively refresh token if expired (except for auth endpoints)
    if (!endpoint.includes('/auth/') && isTokenExpired()) {
      await this.tryRefreshToken();
    }

    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    // Add auth token if available
    const token = getAccessToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    // Add tenant ID if set
    const tenantId = useAuthStore.getState().currentTenant?.tenantId;
    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-ID'] = tenantId;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    const data = await response.json() as ApiResponse<T>;

    if (!response.ok) {
      // Handle 401 - token expired
      if (response.status === 401 && retry && !endpoint.includes('/auth/')) {
        // Try to refresh the token
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          // Retry the request with new token
          return this.request<T>(endpoint, options, false);
        }

        // Refresh failed, redirect to login
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }

      throw new ApiError(data as ApiResponse<never>);
    }

    return data;
  }

  private async tryRefreshToken(): Promise<boolean> {
    // If already refreshing, wait for it
    if (isRefreshing && refreshPromise) {
      return refreshPromise;
    }

    const { refreshToken } = useAuthStore.getState();
    if (!refreshToken) {
      return false;
    }

    isRefreshing = true;
    refreshPromise = (async () => {
      try {
        const response = await fetch(`${this.baseUrl}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!response.ok) {
          return false;
        }

        const data = await response.json() as ApiResponse<{ tokens: { accessToken: string; refreshToken: string; expiresIn: number } }>;
        if (!data.success || !data.data) {
          return false;
        }

        useAuthStore.getState().setTokens(data.data.tokens);
        return true;
      } catch {
        return false;
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async delete<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'DELETE',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  async postFormData<T>(endpoint: string, formData: FormData, retry = true): Promise<ApiResponse<T>> {
    // Proactively refresh token if expired (except for auth endpoints)
    if (!endpoint.includes('/auth/') && isTokenExpired()) {
      await this.tryRefreshToken();
    }

    const url = `${this.baseUrl}${endpoint}`;

    const headers: HeadersInit = {};

    // Add auth token if available
    const token = getAccessToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }

    // Add tenant ID if set
    const tenantId = useAuthStore.getState().currentTenant?.tenantId;
    if (tenantId) {
      (headers as Record<string, string>)['X-Tenant-ID'] = tenantId;
    }

    // Note: Don't set Content-Type header - browser will set it with boundary

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData,
    });

    const data = await response.json() as ApiResponse<T>;

    if (!response.ok) {
      // Handle 401 - token expired
      if (response.status === 401 && retry && !endpoint.includes('/auth/')) {
        // Try to refresh the token
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          // Retry the request with new token
          return this.postFormData<T>(endpoint, formData, false);
        }

        // Refresh failed, redirect to login
        useAuthStore.getState().logout();
        window.location.href = '/login';
      }

      throw new ApiError(data as ApiResponse<never>);
    }

    return data;
  }
}

export class ApiError extends Error {
  public code: string;
  public details?: unknown[];

  constructor(response: ApiResponse<never>) {
    super(response.error?.message || 'An error occurred');
    this.name = 'ApiError';
    this.code = response.error?.code || 'UNKNOWN_ERROR';
    this.details = response.error?.details;
  }
}

export const api = new ApiClient(API_URL);
